import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { formatApiError } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, ArrowRight, Building2 } from "lucide-react";

const FEATURES = [
  "MoneyFlex full weekly workout — pay period planner & gig income",
  "SnapBudget Budget vs Actual comparison",
  "Full Morning Briefing command center — all cards unlocked",
  "Tax Clarity Hub — personal deductions & tips",
  "Unlimited reminders & tasks",
];

export default function UpgradeModal({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    setBusy(true);
    try {
      await startCheckout("premium_monthly");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="upgrade-modal" subtitle="Upgrade your plan" title="Go Premium">
      <div className="space-y-5">
        <div className="flex items-end gap-2">
          <span className="font-serif text-5xl tracking-tight">$29.99</span>
          <span className="text-[var(--text-2)] mb-2">/ month</span>
          <Crown className="w-6 h-6 text-[var(--accent)] ml-auto mb-2" />
        </div>

        {user?.premium_active ? (
          <div className="border border-[var(--business)] bg-[var(--business)]/10 p-4 text-sm flex items-center gap-2" data-testid="already-premium">
            <Check className="w-5 h-5 text-[var(--business)]" /> {user?.platinum_active ? "Business Platinum is active — you have everything." : "Premium is active on your account."}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[var(--text-2)]">7-day free trial · no charge today. Secure checkout by Stripe.</p>
            <button
              data-testid="subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              className="group w-full h-12 bg-[var(--accent)] text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? "Redirecting…" : "Start 7-day free trial"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </>
        )}

        {!user?.platinum_active && (
          <button
            data-testid="see-platinum-btn"
            onClick={() => { onClose && onClose(); navigate("/pricing"); }}
            className="w-full h-11 border border-[var(--line)] text-sm flex items-center justify-center gap-2 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <Building2 className="w-4 h-4" /> Compare plans · Business Platinum $69.99/mo
          </button>
        )}
      </div>
    </Modal>
  );
}
